import { ShoppingBag, Building2, TrendingUp, Laptop, Store, Heart, Landmark } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import caseStudyEcommerce from "@/assets/case-study-ecommerce.jpg";
import caseStudyB2B from "@/assets/case-study-b2b.jpg";
import caseStudySaas from "@/assets/case-study-saas.jpg";
import caseStudyRetail from "@/assets/case-study-retail.jpg";
import caseStudyHealth from "@/assets/case-study-health.jpg";
import caseStudyFinance from "@/assets/case-study-finance.jpg";

const CaseStudies = () => {
  const { t, language } = useLanguage();
  const isMobile = useIsMobile();
  
  const cases = [
    {
      icon: ShoppingBag,
      gradient: "from-sky-500 to-blue-600",
      image: caseStudyEcommerce,
      statValue: "+312%",
      statLabel: language === "fr" ? "visibilité IA" : "AI visibility",
      context: language === "fr" ? "E-commerce mode cherchant à augmenter sa présence dans les recommandations IA" : "Fashion e-commerce seeking to increase AI recommendation presence",
      result: language === "fr" ? "Leader des citations IA en 4 mois" : "AI citation leader in 4 months",
    },
    {
      icon: Building2,
      gradient: "from-purple-500 to-violet-600",
      image: caseStudyB2B,
      statValue: "+89%",
      statLabel: language === "fr" ? "citations IA" : "AI citations",
      context: language === "fr" ? "Cabinet B2B souhaitant être recommandé par ChatGPT et Perplexity" : "B2B firm wanting ChatGPT and Perplexity recommendations",
      result: language === "fr" ? "Top 3 des recommandations sectorielles" : "Top 3 sector recommendations",
    },
    {
      icon: Laptop,
      gradient: "from-teal-500 to-cyan-600",
      image: caseStudySaas,
      statValue: "+156%",
      statLabel: language === "fr" ? "leads qualifiés" : "qualified leads",
      context: language === "fr" ? "Startup SaaS B2B visant les décideurs tech via l'IA générative" : "B2B SaaS startup targeting tech decision-makers via generative AI",
      result: language === "fr" ? "Pipeline commercial doublé en 6 mois" : "Sales pipeline doubled in 6 months",
    },
    {
      icon: Store,
      gradient: "from-amber-500 to-orange-600",
      image: caseStudyRetail,
      statValue: "+45%",
      statLabel: language === "fr" ? "visibilité locale" : "local visibility",
      context: language === "fr" ? "Enseigne retail luxe cherchant à capter les requêtes locales IA" : "Luxury retail brand seeking to capture local AI queries",
      result: language === "fr" ? "Première recommandation locale en 3 mois" : "First local recommendation in 3 months",
    },
    {
      icon: Heart,
      gradient: "from-emerald-500 to-green-600",
      image: caseStudyHealth,
      statValue: "+234%",
      statLabel: language === "fr" ? "mentions IA" : "AI mentions",
      context: language === "fr" ? "Clinique santé souhaitant être citée dans les réponses médicales IA" : "Health clinic wanting citations in AI medical responses",
      result: language === "fr" ? "Référence santé dans 5 régions" : "Health reference in 5 regions",
    },
    {
      icon: Landmark,
      gradient: "from-indigo-500 to-blue-600",
      image: caseStudyFinance,
      statValue: "+124%",
      statLabel: language === "fr" ? "conversions" : "conversions",
      context: language === "fr" ? "Fintech visant à dominer les recommandations d'investissement IA" : "Fintech aiming to dominate AI investment recommendations",
      result: language === "fr" ? "Top 2 des citations fintech IA" : "Top 2 AI fintech citations",
    },
  ];

  const CaseCard = ({ caseStudy, index }: { caseStudy: typeof cases[0]; index: number }) => (
    <div className="group relative rounded-xl overflow-hidden border border-slate-200 bg-white hover:shadow-xl transition-all duration-300 h-full">
      {/* Glass shine effect - subtle */}
      <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none z-30">
        <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
      </div>
      {/* Image de fond */}
      <div className="relative h-32 overflow-hidden">
        <img 
          src={caseStudy.image} 
          alt={`Étude de cas - ${caseStudy.context}`}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/60 to-transparent" />
        
        {/* Stat overlay */}
        <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-lg border border-slate-100">
          <div className={`text-lg font-display font-bold bg-gradient-to-r ${caseStudy.gradient} bg-clip-text text-transparent`}>
            {caseStudy.statValue}
          </div>
          <div className="text-[9px] text-slate-500">{caseStudy.statLabel}</div>
        </div>
        
        {/* Icon */}
        <div className={`absolute bottom-3 left-3 w-9 h-9 rounded-xl bg-gradient-to-br ${caseStudy.gradient} flex items-center justify-center shadow-lg`}>
          <caseStudy.icon className="h-4 w-4 text-white" />
        </div>
      </div>

      {/* Content compact */}
      <div className="p-3 space-y-1.5">
        <div className="flex items-start gap-2">
          <span className="text-[9px] font-semibold text-sky-600 uppercase tracking-wide min-w-[45px]">
            {language === "fr" ? "Contexte" : "Context"}
          </span>
          <p className="text-[11px] text-slate-600 flex-1 line-clamp-2">
            {caseStudy.context}
          </p>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-[9px] font-semibold text-emerald-600 uppercase tracking-wide min-w-[45px]">
            {language === "fr" ? "Résultat" : "Result"}
          </span>
          <p className="text-[11px] font-medium text-slate-700 flex-1">
            {caseStudy.result}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <section id="cases" className="py-10 lg:py-12 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
          </div>
          <h2 className="text-xl sm:text-2xl font-display font-bold text-slate-800">
            {language === "fr" ? "Résultats clients" : "Client results"}
          </h2>
        </div>
      </div>

      {/* Auto-scrolling marquee */}
      <div className="relative">
        {/* Fade edges */}
        <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
        
        <div className="flex gap-4 animate-marquee-left hover:[animation-play-state:paused]">
          {/* Double the items for seamless loop */}
          {[...cases, ...cases].map((caseStudy, index) => (
            <div key={index} className="flex-shrink-0 w-[300px]">
              <CaseCard caseStudy={caseStudy} index={index % cases.length} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
